<template>
  <LoadingIndicator v-if="isLoading" />
  <cms-media-custom-html
    v-else-if="hasContent"
    :theme="themeOptions"
    :content="content"
  />
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useMainStore } from '../../../../stores/mainStore.js';
import { useTranslationStore } from '../../../../stores/translationStore.js';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import LoadingIndicator from '../../../global/LoadingIndicator.vue';
import HomepageFetchHelper from '../../../../helpers/homepage-fetcher';

defineOptions({
  name: 'ViewMediaCode',
});

const props = defineProps({
  containerId: {
    type: String,
    default: null,
  },
  containerIdx: {
    type: Number,
    default: null,
  },
  componentIdx: {
    type: Number,
    default: null,
  },
});

const mainStore = useMainStore();
const translationStore = useTranslationStore();

const nuxtGlobs = { axios: null, staticFetch: null };

const isLoading = ref(false);
const content = ref([]);

const theme = computed(() => mainStore.theme);
const pathPrefix = computed(() => mainStore.pathPrefix);
const customSectionPaths = computed(() => mainStore.customSectionPaths);
const settings = computed(() => mainStore.settings);
const featureFlags = computed(() => mainStore.featureFlags);
const staticFilesConfig = computed(() => mainStore.staticFilesConfig);
const globals = computed(() => mainStore.globals);
const locale = computed(() => translationStore.locale);

const themeOptions = computed(() => {
  if (!theme.value?.containers) return {};
  return {
    ...theme.value.containers[props.containerIdx].components[props.componentIdx].options,
    translations: translationStore.getComponentTranslation('media_custom_code'),
  };
});

const customSectionInfo = computed(() => themeOptions.value.section);

const customSectionType = computed(() => mapSectionToPath(customSectionInfo.value.type));

const sectionHasExpirableEvents = computed(() => {
  const timeSensitiveCustomSections = ['athletics', 'events'];
  return timeSensitiveCustomSections.includes(customSectionType.value);
});

const useS3ForMedia = computed(() => {
  const flag = `cms_stored_data_${customSectionType.value}_enabled`;
  return featureFlags.value[flag];
});

const fallbackImage = computed(() => {
  const liveFeedDefaultFallbackImage =
    globals.value?.fallback_images?.find((item) => item.feature === 'live_feed') || {};
  const newsDefaultFallbackImage = globals.value?.fallback_images?.find((item) => item.feature === 'news') || {};

  if (!themeOptions.value || !settings.value) return '';
  const newsFallbackImg =
    Object.keys(settings.value?.newsFallbackImage || {}).length === 0
      ? newsDefaultFallbackImage
      : settings.value.newsFallbackImage;
  const liveFeedFallbackImg =
    Object.keys(settings.value?.liveFeedFallbackImage || {}).length === 0
      ? liveFeedDefaultFallbackImage
      : settings.value.liveFeedFallbackImage;
  if (customSectionType.value === 'news') return newsFallbackImg;
  if (customSectionType.value === 'live_feed') return liveFeedFallbackImg;
  return '';
});

const hasContent = computed(() => !!content.value?.entries?.length);

function mapSectionToPath(cstype) {
  switch (cstype) {
    case 'live_feeds':
      return 'live_feed';
    case 'articles':
      return 'news';
    case 'menus':
      return 'dining';
    case 'scores_schedules':
      return 'athletics';
    case 'directories':
      return 'staff';
    case 'all_events':
      return 'events';
    default:
      return cstype;
  }
}

function filterEventsBySectionIds(events) {
  const filterID = customSectionInfo.value.id;
  if (filterID) {
    return events.filter((event) => event.custom_section_id === filterID);
  }
  return events;
}

function createCustomSectionLinks(customSection, paths) {
  const urlBase = `v4.${customSectionType.value}`;
  const defaultPath = `${urlBase}.main`;
  const customSectionPath =
    customSection.type === 'all_events' ? defaultPath : `${urlBase}.sections.${kebabCase(customSection.name)}`;
  const customSectionURL = get(paths, customSectionPath, get(paths, defaultPath, get(paths, customSectionType.value)));

  return { ...customSection, customSectionURL };
}

async function fetchEntriesFromS3() {
  try {
    const s3UrlForContent = mainStore.getS3UrlForMedia(customSectionType.value);
    const contentResponse = await nuxtGlobs.staticFetch.get(s3UrlForContent);

    const lastS3FileUpdate = contentResponse.data?.meta?.last_static_update;
    const lastMediaUpdate =
      staticFilesConfig.value?.last_static_update?.[customSectionInfo.value.api] ||
      staticFilesConfig.value?.homepage?.last_static_update?.[customSectionInfo.value.api];
    const isStaticDataFresh = HomepageFetchHelper.validateLastStaticUpdate(lastS3FileUpdate, lastMediaUpdate);

    const sectionData =
      customSectionInfo.value.type === 'events'
        ? filterEventsBySectionIds(contentResponse.data?.[customSectionInfo.value.api])
        : contentResponse.data?.[customSectionInfo.value.api];
    const hasOutDatedEntries = sectionHasExpirableEvents.value
      ? HomepageFetchHelper.hasOutdatedEntries(sectionData, customSectionType.value)
      : false;
    if (!isStaticDataFresh || !sectionData || hasOutDatedEntries) {
      return await fetchEntriesFromThrillshare();
    }
    return {
      section_name: customSectionInfo.value.name,
      entries: sectionData,
      path_prefix: pathPrefix.value,
      fallback_image: fallbackImage.value,
    };
  } catch (e) {
    console.error(e);
    return await fetchEntriesFromThrillshare();
  }
}

async function fetchEntriesFromThrillshare() {
  try {
    const { customSectionURL } = createCustomSectionLinks(customSectionInfo.value, customSectionPaths.value);
    const sectionData = await nuxtGlobs.axios.get(customSectionURL, {
      params: {
        locale: locale.value,
      },
    });
    return {
      section_name: customSectionInfo.value.name,
      entries: sectionData.data[customSectionInfo.value.api],
      path_prefix: pathPrefix.value,
      fallback_image: fallbackImage.value,
    };
  } catch (e) {
    console.error(e);
    return null;
  }
}

onMounted(async () => {
  const inst = getCurrentInstance();
  const p = inst?.proxy;
  const gp = inst?.appContext.config.globalProperties;
  nuxtGlobs.axios = p?.$axios ?? gp?.$axios;
  nuxtGlobs.staticFetch = p?.$staticFetch ?? gp?.$staticFetch;

  isLoading.value = true;
  if (useS3ForMedia.value) {
    content.value = await fetchEntriesFromS3();
  } else {
    content.value = await fetchEntriesFromThrillshare();
  }
  isLoading.value = !content.value;
});
</script>
